import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FancyTree from "../shared/FancyTree";
import Select from "react-select";
import axios from "axios";
import SuccessPopup from "../shared/SuccessPopup.jsx";
import { useLoading } from "../../context/LoadingContext.jsx";
import GlobalLoader from "../shared/GlobalLoader.jsx";
import DocStorePermissionCheckboxes from "./DocStorePermissionCheckboxes.jsx";

const UserAccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const myData = location.state || {};
  const accessToken = sessionStorage.getItem("accessToken");
  const userDetails = JSON.parse(localStorage.getItem("userDetails"));
  const [docPermissions, setDocPermissions] = useState(
    myData?.user?.docStorePermissions || []
  );
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [treeData, setTreeData] = useState([]);
  const [adminCustomers, setAdminCustomers] = useState([]);
  const [customer, setCustomer] = useState("");
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [showSuccessPopup, setShowSuccessPopup] = useState({
    type: "",
    show: false,
    message: "",
  });
  const { isLoading, setLoading } = useLoading();

  const headers = accessToken
    ? {
        Authorization: `${accessToken}`,
        "Content-Type": "application/json",
      }
    : { "Content-Type": "application/json" };

  const [featurePermissions, setFeaturePermissions] = useState([]);
  const [
    customerLocationSystemsPermission,
    setCustomerLocationSystemsPermission,
  ] = useState([]);

  // Initialize state from props only once on component mount
  useEffect(() => {
    if (myData?.user) {
      setFeaturePermissions(
        Array.isArray(myData.user.featurePermissions)
          ? [...myData.user.featurePermissions]
          : []
      );

      setCustomerLocationSystemsPermission(
        Array.isArray(myData.user.customerLocationSystemsPermission)
          ? [...myData.user.customerLocationSystemsPermission]
          : []
      );
    }
  }, [myData]);

  useEffect(() => {
    setLoading(true);
    const fetchAdminCustomers = async () => {
      try {
        const userId = parseInt(userDetails?.id);
        const response = await axios.post(
          `${baseUrl}/customer`,
          { userId },
          { headers }
        );

        const customerList = response?.data || [];
        setAdminCustomers(customerList);

        if (customerList.length > 0) {
          const storedCustomerId = localStorage.getItem("customerId");
          if (storedCustomerId) {
            const storedCustomer = customerList.find(
              (c) => c.id === parseInt(storedCustomerId)
            );
            setCustomer(
              storedCustomer ? storedCustomer.name : customerList[0].name
            );
          } else {
            setCustomer(customerList[0].name);
          }
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Error fetching admin customer data:", error);
      }
    };

    fetchAdminCustomers();
  }, []);

  const handleChange = (selected) => {
    setSelectedCustomers(selected || []);
    if (!selected || selected.length === 0) {
      setCustomerLocationSystemsPermission([]);
    }
  };

  const customerOptions = useMemo(() => {
    return (adminCustomers || []).map((c) => ({
      label: c.name,
      value: c.id,
    }));
  }, [adminCustomers]);

  const getCustomerLocationWithSystems = async (customerId) => {
    try {
      const response = await axios.get(
        `${baseUrl}/customer/${customerId}/locationSystems`,
        { headers }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching systems:", error);
      return [];
    }
  };

  // Fetch locations + systems when selectedCustomers change
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const allData = [];
        const idToKeyMap = {};

        for (const customer of selectedCustomers) {
          const customerData = await getCustomerLocationWithSystems(
            customer.value
          );

          const formatted = customerData?.map((location) => {
            const locKey = `loc-${location.id}`;
            return {
              name: location.plant_name,
              id: location.id,
              key: locKey,
              value: locKey,
              children: (location.systems || []).map((system) => {
                const sysKey = `sys-${system.id}`;
                return {
                  name: system.system_name,
                  id: system.id,
                  key: sysKey,
                  value: sysKey,
                };
              }),
            };
          });

          allData.push({
            name: customer.label,
            id: customer.value,
            key: `cust-${customer.value}`,
            value: `cust-${customer.value}`,
            children: formatted,
          });
        }

        allData.forEach((customer) => {
          idToKeyMap[customer.id] = customer.key;
          (customer.children || []).forEach((location) => {
            idToKeyMap[location.id] = location.key;
            (location.children || []).forEach((system) => {
              idToKeyMap[system.id] = system.key;
            });
          });
        });

        const updatedPermissions = customerLocationSystemsPermission.map(
          (perm) => ({
            ...perm,
            key: idToKeyMap[perm.entityId] || null,
          })
        );
        const filteredPermissions = updatedPermissions
          .filter((perm) => perm.key !== null)
          .map(({ key, ...rest }) => rest);
        setCustomerLocationSystemsPermission(filteredPermissions);
        setTreeData(allData);
      } catch (error) {
        console.error("Failed to fetch tree data:", error);
        setTreeData([]);
      } finally {
        setLoading(false);
      }
    };

    if (selectedCustomers.length > 0) {
      fetchData();
    } else {
      setTreeData([]);
      setLoading(false);
    }
  }, [selectedCustomers]);

  const extractIdsFromPermissions = (permissions, customersData) => {
    const custIdSet = new Set();
    const locIdSet = new Set();
    const sysIdSet = new Set();

    for (const perm of permissions) {
      if (perm.entityType === "CUSTOMER") {
        const customerMatch = customersData?.find(
          (c) => c.id === perm.entityId
        );
        if (customerMatch) {
          custIdSet.add(perm.entityId);
          continue;
        }
      }

      if (perm.entityType === "LOCATION") {
        locIdSet.add(perm.entityId);
      }

      if (perm.entityType === "SYSTEM") {
        sysIdSet.add(perm.entityId);
      }
    }

    return {
      customerIds: Array.from(custIdSet),
      locationIds: Array.from(locIdSet),
      systemIds: Array.from(sysIdSet),
    };
  };

  useEffect(() => {
    if (
      adminCustomers.length === 0 ||
      !myData?.user?.customerLocationSystemsPermission
    )
      return;
    setLoading(true);

    const { customerIds } = extractIdsFromPermissions(
      myData.user.customerLocationSystemsPermission,
      adminCustomers
    );

    const selected = adminCustomers
      .filter((c) => customerIds.includes(c.id))
      .map((c) => ({
        label: c.name,
        value: c.id,
      }));

    setSelectedCustomers(selected);
    setLoading(false);
  }, [adminCustomers, myData?.user?.customerLocationSystemsPermission]);

  useEffect(() => {
    const fetchData = async () => {
      const allData = [];

      for (const customer of selectedCustomers) {
        const customerData = await getCustomerLocationWithSystems(
          customer.value
        );

        const formatted = customerData?.map((location) => {
          return {
            name: location.plant_name,
            id: location.id,
            key: `loc-${location.id}`,
            value: `loc-${location.id}`,
            children: (location.systems || []).map((system) => ({
              name: system.system_name,
              id: system.id,
              key: `sys-${system.id}`,
              value: `sys-${system.id}`,
            })),
          };
        });

        allData.push({
          name: customer.label,
          id: customer.value,
          key: `cust-${customer.value}`,
          value: `cust-${customer.value}`,
          children: formatted,
        });
      }

      setTreeData(allData);
      setLoading(false);
    };

    if (selectedCustomers.length > 0) {
      setLoading(true);
      fetchData();
    } else {
      setTreeData([]);
      setLoading(false);
    }
  }, [selectedCustomers]);

  const handleBackToUserManagement = () => {
    navigate("/user-management");
  };

  const updateAccessControlData = async () => {
    if (!myData?.user?.id) {
      console.error("User ID is missing");
      return;
    }

    const validFeaturePermissions = featurePermissions.filter(
      (perm) => perm !== undefined && perm !== null
    );

    const validCustomerPermissions = customerLocationSystemsPermission.filter(
      (perm) => perm && perm.entityType && perm.entityId
    );

    try {
      const userUpdateResponse = await axios.put(
        `${baseUrl}/user/${myData?.user?.id}`,
        {
          featurePermissions: validFeaturePermissions,
          customerLocationSystemsPermission: validCustomerPermissions,
          docStorePermissions: docPermissions,
        },
        { headers }
      );

      if (userUpdateResponse.status === 200) {
        setShowSuccessPopup({
          type: "success",
          show: true,
          message: "Access control updated successfully",
        });
      }
    } catch (error) {
      console.error("Error updating access control:", error);
      setShowSuccessPopup({
        type: "warning",
        show: true,
        message: "Something went wrong. Please try again later",
      });
    }
  };

  const encodeKey = ({ entityType, entityId }) => {
    const prefix =
      entityType === "CUSTOMER"
        ? "cust"
        : entityType === "LOCATION"
        ? "loc"
        : entityType === "SYSTEM"
        ? "sys"
        : "";
    return `${prefix}-${entityId}`;
  };

  const decodeKey = (key) => {
    const [prefix, id] = key.split("-");
    return {
      entityType:
        prefix === "cust"
          ? "CUSTOMER"
          : prefix === "loc"
          ? "LOCATION"
          : "SYSTEM",
      entityId: parseInt(id, 10),
    };
  };

  return (
    <div className="container-fluid">
      {isLoading && <GlobalLoader />}
      <div className="row rounded mx-2">
        <div className="col-12">
          <div className="px-2 d-flex justify-content-end">
            <button
              type="submit"
              className="btn btn-primary py-2 px-3 fw-500 d-flex justify-content-center align-items-center"
              onClick={() => handleBackToUserManagement()}
            >
              All Users
            </button>
          </div>
        </div>
      </div>

      <div className="row rounded mx-2 mt-2">
        <div className="col-6">
          <div className="row">
            <div className="col-12 mt-2">
              <div className="theme-container rounded custom-shadow px-2 py-3">
                <div className="fnt15 fw-600">Page Access</div>
                {myData?.user && myData?.user?.access_control && (
                  <div className="fnt12 fw-400 px-2 py-3">
                    <FancyTree
                      data={JSON.parse(myData?.user?.access_control) || "[]"}
                      onChange={setFeaturePermissions}
                      initialChecked={myData?.user?.featurePermissions || []}
                      disabled={false}
                      mode="feature"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="col-12 mt-2">
              <div className="theme-container rounded custom-shadow px-2 py-3">
                <DocStorePermissionCheckboxes
                  enabled={true}
                  value={docPermissions}
                  onChange={setDocPermissions}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-6 mt-2">
          <div className="theme-container rounded custom-shadow px-2 py-3">
            <div className="fnt15 fw-600">Plant Or Tree Access</div>
            <div className="col-12 mt-2">
              <label className="fw-500">Select Customer(s)</label>
              <Select
                isMulti
                isClearable
                options={customerOptions}
                value={selectedCustomers}
                onChange={handleChange}
                className="mb-3"
                styles={{
                  option: (provided) => ({
                    ...provided,
                    color: "black",
                  }),
                }}
              />
            </div>
            <div>
              <div className="mt-3">
                <FancyTree
                  data={treeData}
                  onChange={(newChecked) =>
                    setCustomerLocationSystemsPermission(
                      newChecked.map(decodeKey)
                    )
                  }
                  initialChecked={customerLocationSystemsPermission.map(
                    encodeKey
                  )} // parent controls
                  disabled={false}
                  mode="entity"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="py-4 d-flex justify-content-end">
            <button
              type="submit"
              id="userAccessPageSetPermissions"
              className="btn btn-primary py-2 px-3 fw-500 d-flex justify-content-center align-items-center"
              onClick={() => updateAccessControlData()}
            >
              Set Permissions
            </button>
          </div>
        </div>
      </div>
      <SuccessPopup
        type={showSuccessPopup?.type}
        show={showSuccessPopup?.show}
        message={showSuccessPopup?.message}
        onClose={() => {
          setShowSuccessPopup(false);
          if (showSuccessPopup.type === "success") {
            navigate("/user-management");
          }
        }}
      />
    </div>
  );
};

export default UserAccessPage;
